import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

import { listIndustrialRefBook } from '@/api/endpoints/industrialRefBook';
import type { IndustrialRefBookRecord } from '@/types/industrialRefBook';
import { extractAxiosMessage } from '@/utils/axiosError';

const DEFAULT_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

export const INDUSTRIAL_REF_BOOK_PAGE_SIZE = DEFAULT_PAGE_SIZE;

export type IndustrialRefBookStatus = 'idle' | 'loading' | 'success' | 'error';

export interface UseIndustrialRefBookLibraryValue {
  readonly items: readonly IndustrialRefBookRecord[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
  readonly status: IndustrialRefBookStatus;
  readonly errorMessage: string | null;
  readonly search: string;
  readonly setSearch: (next: string) => void;
  readonly setPage: (page: number) => void;
  readonly refresh: () => void;
}

export function useIndustrialRefBookLibrary(): UseIndustrialRefBookLibraryValue {
  const [search, setSearchState] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<readonly IndustrialRefBookRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState<IndustrialRefBookStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [reloadCounter, setReloadCounter] = useState(0);
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    setStatus('loading');
    setErrorMessage(null);
    listIndustrialRefBook({
      limit: DEFAULT_PAGE_SIZE,
      offset: (page - 1) * DEFAULT_PAGE_SIZE,
      search: debouncedSearch,
      signal: controller.signal,
    })
      .then((response) => {
        if (controller.signal.aborted) return;
        setItems(response.items);
        setTotal(response.total);
        setStatus('success');
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted || axios.isCancel(error)) return;
        setStatus('error');
        setErrorMessage(extractAxiosMessage(error) ?? 'unknown error');
      });

    return () => controller.abort();
  }, [page, debouncedSearch, reloadCounter]);

  const setSearch = useCallback((next: string) => setSearchState(next), []);
  const refresh = useCallback(() => setReloadCounter((v) => v + 1), []);

  return {
    items,
    total,
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    status,
    errorMessage,
    search,
    setSearch,
    setPage,
    refresh,
  };
}
